/**
 * Responsive — the one place the application decides what "mobile" means.
 *
 * NFR-05 asks for desktop at reception and administration, tablet for the doctor
 * and the specialists. This module draws the line at Tailwind's `lg` (1024px):
 * a laptop and an iPad in landscape get the dense desktop shell that DS-01…08
 * describes, a phone and an iPad in portrait get the compact one.
 *
 * The number lives here and in the `max-lg:` variants across the styles, and
 * nowhere else. Tailwind's `lg` screen is the source of truth for both — a
 * component that branches in JavaScript and a rule that branches in CSS must
 * agree on the same pixel, or the sidebar disappears while the content is still
 * laid out around it.
 *
 * `1023.98px` rather than `1023px`: a fractional viewport width (browser zoom,
 * a hi-dpi Android in CSS pixels) sits between the two integers, and `max-width:
 * 1023px` would hand it the desktop shell while `max-lg:` — which Tailwind emits
 * as `not all and (min-width: 1024px)` — hands it the mobile one.
 */

import { useSyncExternalStore } from 'react';

/** Tailwind's `lg`. Mobile is everything strictly below it. */
export const MOBILE_BREAKPOINT = 1024;

/** Matches exactly what `max-lg:` matches in the stylesheet. */
export const MOBILE_MEDIA_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 0.02}px)`;

/** Coarse pointer — a finger rather than a mouse. Independent of width: a large touchscreen is still touched. */
export const TOUCH_MEDIA_QUERY = '(pointer: coarse)';

/** One `MediaQueryList` per query, shared by every hook that asks for it. */
const lists = new Map<string, MediaQueryList>();

function listFor(query: string): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  let list = lists.get(query);
  if (!list) {
    list = window.matchMedia(query);
    lists.set(query, list);
  }
  return list;
}

/**
 * Subscribes to a media query the way `useOnlineStatus` subscribes to the
 * connection: the browser already tracks it, so there is nothing to poll and
 * nothing to measure on resize.
 */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const list = listFor(query);
      if (!list) return () => {};
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    () => listFor(query)?.matches ?? false,
    // No window means no viewport to be narrow. Falling back to the desktop
    // shell keeps the pre-hydration tree identical to what a desktop paints.
    () => false,
  );
}

/**
 * True below 1024px. Every component that branches on layout — the shell, the
 * dialogs, the tables — asks this and nothing else, so there is a single answer
 * in the tree at any moment.
 */
export function useIsMobile(): boolean {
  return useMediaQuery(MOBILE_MEDIA_QUERY);
}

/** True where the primary pointer is a finger. Used to widen hit areas, never to change layout. */
export function useIsTouch(): boolean {
  return useMediaQuery(TOUCH_MEDIA_QUERY);
}
